import { useEffect, useMemo, useState } from 'react'
import { Timeline } from '../ui/Timeline'
import { CategoryRail } from '../ui/CategoryRail'
import { RecordingPill } from '../ui/RecordingPill'
import { BlockEditor } from '../ui/BlockEditor'
import { PeriodNoteBar } from '../ui/PeriodNoteBar'
import { Totals } from '../ui/Totals'
import { PeriodNav } from '../ui/PeriodNav'
import { useKnownTags, useRangeBlocks } from '../ui/useBlockActions'
import { usePaintCategory } from '../ui/usePaintCategory'
import { useWorkspace } from '../data/workspace'
import { activeCategories } from '../data/settings'
import { excludedCategories } from '../data/insights'
import { EMPTY_NOTE, subscribePeriodNote, writePeriodNote, type PeriodNoteValue } from '../data/periodNotes'
import { dayRange, shiftDayKey, todayKey } from '../lib/time'
import { formatDayLabel } from '../lib/format'
import type { Block } from '../data/types'

export function TodayPage() {
  const { uid, taxonomy, categories, settings, openBlock } = useWorkspace()
  const [day, setDay] = useState(() => todayKey(settings.dayStartHour))
  const [editing, setEditing] = useState<Block | null>(null)
  const [note, setNote] = useState<PeriodNoteValue>(EMPTY_NOTE)

  const palette = activeCategories(taxonomy)
  const [paintId, setPaintId] = usePaintCategory(palette, openBlock)
  const excluded = useMemo(() => excludedCategories(categories), [categories])
  const range = useMemo(() => dayRange(day, settings.dayStartHour), [day, settings.dayStartHour])
  const { blocks, error, create, resize, move, moveBoundary, limitsFor, edit, quickPatch, split, remove } =
    useRangeBlocks(uid, range.start, range.end)
  const knownTags = useKnownTags(uid)

  useEffect(() => subscribePeriodNote(uid, 'day', day, setNote), [uid, day])


  return (
    <div className="mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col lg:max-w-5xl gap-2.5 px-4 pt-3 pb-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <PeriodNav
          label={formatDayLabel(day, 'EEEE d MMM')}
          onPrevious={() => setDay(shiftDayKey(day, -1))}
          onNext={() => setDay(shiftDayKey(day, 1))}
          onToday={() => setDay(todayKey(settings.dayStartHour))}
          isCurrent={day === todayKey(settings.dayStartHour)}
        />
        {openBlock ? (
          <RecordingPill block={openBlock} category={categories.get(openBlock.categoryId)} />
        ) : null}
        <CategoryRail categories={palette} selectedId={paintId} onSelect={setPaintId} />
      </div>

      <PeriodNoteBar
        label="Day"
        value={note}
        onSave={(value) => void writePeriodNote(uid, 'day', day, value)}
      />

      {error ? <p className="text-[13px] text-danger">{error}</p> : null}

      <div className="flex min-h-0 flex-1 overflow-hidden rounded-xl border border-line bg-surface">
        <Timeline
          days={[day]}
          blocks={blocks}
          categories={categories}
          palette={palette}
          knownTags={knownTags}
          dayStartHour={settings.dayStartHour}
          workdayStartHour={settings.workdayStartHour}
          workdayEndHour={settings.workdayEndHour}
          paintCategoryId={paintId}
          showDayHeaders={false}
          onCreate={(bounds) => paintId && void create(bounds, paintId)}
          onResize={(block, bounds) => void resize(block, bounds)}
          onMoveBoundary={(previous, next, at) => void moveBoundary(previous, next, at)}
          onMoveBlock={(block, delta) => void move(block, delta)}
          onSelectBlock={setEditing}
          onQuickPatch={(block, patch) => void quickPatch(block, patch)}
          onSplitBlock={(block, at) => void split(block, at)}
          onDeleteBlock={(block) => void remove(block)}
        />
      </div>

      <Totals
        blocks={blocks}
        categories={categories}
        from={range.start.getTime()}
        to={range.end.getTime()}
        excluded={excluded}
      />

      {editing ? (
        <BlockEditor
          block={editing}
          categories={palette}
          limits={limitsFor(editing)}
          knownTags={knownTags}
          onSave={(next) => {
            void edit(editing, next)
            setEditing(null)
          }}
          onDelete={() => {
            void remove(editing)
            setEditing(null)
          }}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  )
}
